import React from 'react';
import {useForm, Controller} from 'react-hook-form';
import {
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
} from '@material-ui/core';
import {IFdProductionPlan, IFdCrop} from 'constants/interfaces';
import {EIrrigation} from 'constants/index';

export type Submission = {
  id: string;
  cropId: string;
  numberOfHectares: number;
  estimatedYield: number;
  estimatedPrice: number;
  plantingDate: string;
  notes?: string;
  plantPopulation: number;
  irrigation: EIrrigation;
  areaOfDryLand?: number;
  areaUnderIrrigation?: number;
};

export function FdProductionPlanForm(props: {
  productionPlan?: Partial<IFdProductionPlan>;
  crops: IFdCrop[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (plan: Partial<Submission>) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  readonly?: boolean;
}) {
  const {handleSubmit, control} = useForm<Partial<Submission>>({
    defaultValues: {
      ...props.productionPlan,
      cropId: props.productionPlan?.crop?.id,
      plantingDate: props.productionPlan?.plantingDate
        ? props.productionPlan.plantingDate.split('T')[0]
        : undefined,
      notes: props.productionPlan?.notes || '',
    },
  });

  const onSubmit = (data: Partial<Submission>) => {
    props.onSubmit(data);
  };

  return (
    <div className="generic-form-container fd-production-plan-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Production Plan Details
              </Typography>
            </Grid>

            {/* Crop selector */}
            <Grid item xs={12}>
              <Controller
                name="cropId"
                control={control}
                rules={{required: 'Crop is required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    select
                    className="input"
                    label="Crop"
                    variant="outlined"
                    fullWidth
                    value={value || ''}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  >
                    {props.crops.map((crop) => (
                      <MenuItem key={crop.id} value={crop.id}>
                        {crop.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Number of hectares */}
            <Grid item xs={4}>
              <Controller
                name="numberOfHectares"
                control={control}
                rules={{required: 'Number of hectares is required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Number of Hectares"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value || ''}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Estimated yield */}
            <Grid item xs={4}>
              <Controller
                name="estimatedYield"
                control={control}
                rules={{required: 'Estimated yield is required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Estimated Yield"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value || ''}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Estimated price */}
            <Grid item xs={4}>
              <Controller
                name="estimatedPrice"
                control={control}
                rules={{required: 'Estimated price is required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Estimated Price"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value || ''}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Planting date */}
            <Grid item xs={12}>
              <Controller
                name="plantingDate"
                control={control}
                rules={{required: 'Planting date is required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Planting Date"
                    variant="outlined"
                    fullWidth
                    type="date"
                    value={value || ''}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                    InputLabelProps={{shrink: true}}
                  />
                )}
              />
            </Grid>

            {/* Plant population */}
            <Grid item xs={12}>
              <Controller
                name="plantPopulation"
                control={control}
                rules={{required: 'Plant population is required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Plant Population"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value || ''}
                    onChange={(e) => onChange(parseInt(e.target.value, 10))}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Irrigation selector */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Irrigation Details
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="irrigation"
                control={control}
                rules={{required: 'Irrigation is required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    select
                    className="input"
                    label="Irrigation"
                    variant="outlined"
                    fullWidth
                    value={value || ''}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  >
                    {Object.values(EIrrigation).map((irrigation) => (
                      <MenuItem key={irrigation} value={irrigation}>
                        {irrigation}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Area of dry land */}
            <Grid item xs={6}>
              <Controller
                name="areaOfDryLand"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Area of Dry Land"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value || ''}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Area under irrigation */}
            <Grid item xs={6}>
              <Controller
                name="areaUnderIrrigation"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Area Under Irrigation"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value || ''}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Notes (multiline) */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Notes"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    rowsMax={8}
                    value={value || ''}
                    onChange={onChange}
                    placeholder="Enter any additional notes…"
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>

          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className="secondary-btn"
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className="primary-btn"
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress color="primary" size={24} thickness={6} />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
